import { prepareRecoveryKeyRotation } from '@/crypto/key-hierarchy'
import { ApiError } from '@services/apiClient'
import { sessionStore } from '@features/auth/services/sessionStore'
import { rotateRecoveryKey as defaultRotate } from '../api/recoveryKeyApi'

/**
 * Recovery Key 再生成フロー (Track E-2、KEY_HIERARCHY.md §4.7.2)。
 *
 * 設計判断:
 *   - sessionStore.getState() === 'unlocked' を要求 (SK が必要)。`no_session` で reject
 *   - `prepareRecoveryKeyRotation(SK)` で新 mnemonic + 5 列を組立
 *   - POST `/users/me/recovery-key/rotate` で 5 列を atomic に書き換え
 *   - 成功時: sessionStore は **一切触らない** (SK / accessToken / material は全て不変)
 *   - 文言は messageKey で返却 (CLAUDE.md §2 ルール 10)、UI 層で `t()` 適用
 *   - 失敗時は sessionStore に触らず、新 mnemonic も返さない
 *   - deps DI で network を spec で差し替え可能
 *
 * 戻り値: 成功時のみ `{ mnemonic: string }` を返す。caller は UI で一回限り表示すること
 * (CLAUDE.md §2 ルール 6 厳守、localStorage / IndexedDB / sessionStorage には書かない)。
 */

export class RecoveryKeyRotateError extends Error {
  public readonly code: 'no_session' | 'too_many_requests' | 'network' | 'generic'

  public constructor(code: RecoveryKeyRotateError['code']) {
    super(code)
    this.code = code
    this.name = 'RecoveryKeyRotateError'
  }
}

export interface RecoveryKeyFlowDeps {
  rotate: typeof defaultRotate
}

const defaultDeps: RecoveryKeyFlowDeps = {
  rotate: defaultRotate,
}

export interface RotateRecoveryKeyResult {
  /** 24 単語 (空白区切り)。caller が一回限り表示してから参照を切ること */
  mnemonic: string
}

export const rotateRecoveryKeyFlow = async (
  deps: RecoveryKeyFlowDeps = defaultDeps,
): Promise<RotateRecoveryKeyResult> => {
  const state = sessionStore.getState()
  if (state.kind !== 'unlocked') {
    throw new RecoveryKeyRotateError('no_session')
  }

  // ── 1. crypto で新 mnemonic + 5 列を組立 ─────────────────────────
  const { request, recoveryMnemonic } = await prepareRecoveryKeyRotation({
    symmetricKey: state.symmetricKey,
  })

  // ── 2. POST /users/me/recovery-key/rotate ───────────────────────
  try {
    await deps.rotate(request)
  } catch (err: unknown) {
    if (err instanceof ApiError) {
      if (err.status === 429) throw new RecoveryKeyRotateError('too_many_requests')

      throw new RecoveryKeyRotateError('network')
    }
    throw new RecoveryKeyRotateError('generic')
  }

  // ── 3. 成功時は sessionStore に触らない ─────────────────────────
  // SK / accessToken / material は全て不変。Vault Item / Send / wrappedLinkKey も
  // 引き続き有効 (KEY_HIERARCHY.md §4.7.2)。
  return { mnemonic: recoveryMnemonic }
}
